import { FormEvent, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Calculator,
  CheckCircle2,
  Info,
  Loader2,
  PackageCheck,
  PlayCircle,
  ShieldAlert,
} from 'lucide-react';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import useAlerts from '../../../hooks/useAlerts';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';
import useConfirm from '../../../hooks/useConfirmModal';
import RestaurantProductionInventoryService from '../../../services/restaurant/RestaurantProductionInventoryService';
import {
  DecimalValue,
  RestaurantInventoryConsumptionMoment,
  RestaurantRecipeAvailability,
  RestaurantRecipeConsumption,
  RestaurantRecipeCost,
} from '../../../types/restaurant/RestaurantProductionInventoryTypes';
import {
  compareDecimal,
  formatDecimal,
  formatMoney,
} from '../../../utils/decimalValue';
import { notifyRestaurantInventoryUpdated } from './restaurantInventoryEvents';

interface Props {
  branchId: number;
  recipeId: number;
  initialQuantity: DecimalValue;
  allowConsumption?: boolean;
  onConsumed?: () => void;
}

const momentOptions = [
  { value: 'WHEN_PREPARING', label: 'Al iniciar preparación' },
  { value: 'WHEN_READY', label: 'Al finalizar preparación' },
  { value: 'WHEN_BILLED', label: 'Al facturar' },
];

export const restaurantConsumptionMomentLabel = (
  value: RestaurantInventoryConsumptionMoment
) => momentOptions.find((option) => option.value === value)?.label ?? value;

const RequirementTable = ({
  data,
  availability,
}: {
  data: RestaurantRecipeCost['ingredients'];
  availability?: boolean;
}) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[840px] text-left text-sm">
      <thead className="border-b border-stroke bg-gray-2/60 text-xs uppercase text-bodydark2 dark:border-strokedark dark:bg-meta-4">
        <tr>
          <th className="px-3 py-3">Materia prima</th>
          <th className="px-3 py-3">En receta</th>
          <th className="px-3 py-3">Requerido base</th>
          {availability ? (
            <>
              <th className="px-3 py-3">Stock actual</th>
              <th className="px-3 py-3">Proyectado / mínimo</th>
            </>
          ) : (
            <>
              <th className="px-3 py-3">Costo promedio</th>
              <th className="px-3 py-3">Costo estimado</th>
            </>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-stroke dark:divide-strokedark">
        {data.map((item) => (
          <tr
            key={item.rawMaterialId}
            data-testid={`requirement-${item.rawMaterialId}`}
            className={
              availability && !item.sufficient
                ? 'bg-danger/10 text-danger'
                : availability && item.lowStock
                ? 'bg-warning/10 text-warning'
                : ''
            }
          >
            <td className="px-3 py-3 font-medium">
              {item.rawMaterialCode} · {item.rawMaterialName}
            </td>
            <td className="px-3 py-3">
              {formatDecimal(item.recipeQuantity)} {item.recipeUnitCode}
            </td>
            <td className="px-3 py-3">
              {formatDecimal(item.requiredBaseQuantity)} {item.baseUnitCode}
            </td>
            {availability ? (
              <>
                <td className="px-3 py-3">
                  {formatDecimal(item.currentStock)} {item.baseUnitCode}
                </td>
                <td className="px-3 py-3">
                  {formatDecimal(item.projectedStock)} /{' '}
                  {formatDecimal(item.minimumStock)} {item.baseUnitCode}
                </td>
              </>
            ) : (
              <>
                <td className="px-3 py-3">
                  {formatMoney(item.averageUnitCost)}
                </td>
                <td className="px-3 py-3 font-semibold">
                  {formatMoney(item.estimatedCost)}
                </td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const RestaurantRecipeProductionPanel = ({
  branchId,
  recipeId,
  initialQuantity,
  allowConsumption = false,
  onConsumed,
}: Props) => {
  const [quantity, setQuantity] = useState(String(initialQuantity));
  const [cost, setCost] = useState<RestaurantRecipeCost | null>(null);
  const [availability, setAvailability] =
    useState<RestaurantRecipeAvailability | null>(null);
  const [consumption, setConsumption] =
    useState<RestaurantRecipeConsumption | null>(null);
  const [loadingCost, setLoadingCost] = useState(false);
  const [checking, setChecking] = useState(false);
  const [consuming, setConsuming] = useState(false);
  const [event, setEvent] =
    useState<RestaurantInventoryConsumptionMoment>('WHEN_PREPARING');
  const [referenceType, setReferenceType] = useState('RECIPE_PRODUCTION');
  const [referenceId, setReferenceId] = useState('');
  const [notes, setNotes] = useState('');
  const consumingRef = useRef(false);
  const { handle } = useApiErrorHandler();
  const { show } = useAlerts();
  const confirm = useConfirm();
  const validQuantity =
    quantity.trim() !== '' && compareDecimal(quantity, 0) > 0;

  const loadCost = async (requestedQuantity: DecimalValue = quantity) => {
    if (compareDecimal(requestedQuantity, 0) <= 0) return;
    setLoadingCost(true);
    try {
      setCost(
        (
          await RestaurantProductionInventoryService.calculateCost(
            branchId,
            recipeId,
            requestedQuantity
          )
        ).data
      );
    } catch (error) {
      handle(error);
    } finally {
      setLoadingCost(false);
    }
  };

  const checkAvailability = async () => {
    if (!validQuantity) return null;
    setChecking(true);
    try {
      const data = (
        await RestaurantProductionInventoryService.checkAvailability(
          branchId,
          recipeId,
          quantity
        )
      ).data;
      setAvailability(data);
      return data;
    } catch (error) {
      handle(error);
      return null;
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    setQuantity(String(initialQuantity));
    setCost(null);
    setAvailability(null);
    setConsumption(null);
  }, [branchId, recipeId, initialQuantity]);
  useEffect(() => {
    void loadCost(
      initialQuantity
    ); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [branchId, recipeId, initialQuantity]);

  const executeConsumption = async () => {
    if (consumingRef.current || !validQuantity || !referenceId) return;
    consumingRef.current = true;
    setConsuming(true);
    try {
      const response = await RestaurantProductionInventoryService.consume(
        branchId,
        recipeId,
        {
          quantity,
          event,
          referenceType: referenceType.trim(),
          referenceId: Number(referenceId),
          notes: notes.trim() || null,
        }
      );
      setConsumption(response.data);
      if (response.data.consumed) {
        show(
          response.type || 'success',
          response.title || 'Consumo registrado',
          response.message || 'El inventario fue actualizado.'
        );
        notifyRestaurantInventoryUpdated();
        await Promise.all([loadCost(), checkAvailability()]);
        onConsumed?.();
      } else
        show(
          'info',
          response.title || 'Consumo omitido',
          response.data.skippedReason ||
            response.message ||
            'No se generaron movimientos.'
        );
    } catch (error) {
      handle(error);
    } finally {
      consumingRef.current = false;
      setConsuming(false);
    }
  };

  const requestConsumption = (formEvent: FormEvent) => {
    formEvent.preventDefault();
    if (!validQuantity || !referenceId || consumingRef.current) return;
    confirm({
      title: 'Confirmar consumo de inventario',
      message: `Se descontarán los ingredientes para producir ${quantity}. Esta acción no se puede deshacer.`,
      icon: 'warning',
      confirmText: 'Sí, registrar consumo',
      cancelText: 'Cancelar',
      onConfirm: executeConsumption,
    });
  };

  return (
    <section
      className="space-y-5"
      aria-label="Producción e inventario de receta"
    >
      <div className="rounded-xl border border-stroke dark:border-strokedark">
        <div className="flex flex-col gap-3 border-b border-stroke bg-gray-2/50 p-4 dark:border-strokedark dark:bg-meta-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="flex items-center gap-2 font-semibold text-black dark:text-white">
              <Calculator className="h-5 w-5 text-primary" />
              Costo estimado
            </h3>
            <p className="mt-1 text-xs text-bodydark2">
              Calculado con conversiones y costo promedio vigentes.
            </p>
          </div>
          <div className="flex flex-1 items-end gap-2 sm:max-w-md">
            <InputField
              label="Cantidad a producir"
              name="productionQuantity"
              type="number"
              min={0}
              step="0.000001"
              value={quantity}
              error={!validQuantity ? 'Debe ser mayor que cero.' : undefined}
              onChange={(e) => {
                setQuantity(e.target.value);
                setAvailability(null);
                setCost(null);
              }}
            />
            <button
              type="button"
              onClick={() => void loadCost()}
              disabled={!validQuantity || loadingCost}
              className="mb-4 h-[50px] rounded bg-primary px-4 font-medium text-white disabled:opacity-50"
            >
              {loadingCost ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Calcular'
              )}
            </button>
          </div>
        </div>
        {cost && (
          <div data-testid="recipe-cost">
            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
              <div className="rounded-lg bg-primary/10 p-3">
                <p className="text-xs text-bodydark2">Costo total</p>
                <p className="text-xl font-bold text-primary">
                  {formatMoney(cost.estimatedTotalCost)}
                </p>
              </div>
              <div className="rounded-lg bg-success/10 p-3">
                <p className="text-xs text-bodydark2">Costo unitario</p>
                <p className="text-xl font-bold text-success">
                  {formatMoney(cost.estimatedUnitCost)}
                </p>
              </div>
              <div className="rounded-lg bg-gray-2 p-3 dark:bg-meta-4">
                <p className="text-xs text-bodydark2">Factor de producción</p>
                <p className="text-xl font-bold text-black dark:text-white">
                  {formatDecimal(cost.productionFactor)}
                </p>
              </div>
            </div>
            <RequirementTable data={cost.ingredients} />
          </div>
        )}
      </div>

      <div className="rounded-xl border border-stroke p-4 dark:border-strokedark">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="flex items-center gap-2 font-semibold text-black dark:text-white">
              <PackageCheck className="h-5 w-5 text-primary" />
              Disponibilidad
            </h3>
            <p className="text-xs text-bodydark2">
              Valide existencias antes de registrar la producción.
            </p>
          </div>
          <button
            type="button"
            onClick={checkAvailability}
            disabled={!validQuantity || checking}
            className="inline-flex items-center justify-center gap-2 rounded border border-primary px-4 py-2 font-medium text-primary disabled:opacity-50"
          >
            {checking && <Loader2 className="h-4 w-4 animate-spin" />}Verificar
            disponibilidad
          </button>
        </div>
        {availability && (
          <div className="mt-4 space-y-4" data-testid="availability-result">
            <div
              className={`flex gap-3 rounded-lg border p-4 ${
                availability.available
                  ? 'border-success/30 bg-success/10 text-success'
                  : 'border-danger/30 bg-danger/10 text-danger'
              }`}
            >
              {availability.available ? (
                <CheckCircle2 className="h-5 w-5 shrink-0" />
              ) : (
                <ShieldAlert className="h-5 w-5 shrink-0" />
              )}
              <div>
                <strong>
                  {availability.available
                    ? 'Ingredientes disponibles'
                    : 'Inventario insuficiente'}
                </strong>
                <p className="text-sm">
                  {availability.canProceed
                    ? 'La operación puede continuar.'
                    : 'La operación está bloqueada por la configuración de inventario.'}
                </p>
              </div>
            </div>
            {!availability.available && availability.allowNegativeStock && (
              <div className="flex gap-2 rounded-lg bg-warning/10 p-3 text-sm text-warning">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                Se permite continuar: la sucursal admite existencias negativas.
              </div>
            )}
            {!availability.validationEnabled && (
              <div className="flex gap-2 rounded-lg bg-primary/10 p-3 text-sm text-primary">
                <Info className="h-5 w-5 shrink-0" />
                La validación previa a preparación está deshabilitada.
              </div>
            )}
            <RequirementTable data={availability.ingredients} availability />
          </div>
        )}
      </div>

      {allowConsumption && (
        <form
          onSubmit={requestConsumption}
          className="rounded-xl border border-stroke p-4 dark:border-strokedark"
        >
          <h3 className="flex items-center gap-2 font-semibold text-black dark:text-white">
            <PlayCircle className="h-5 w-5 text-primary" />
            Registrar producción/consumo
          </h3>
          <div className="mt-4 grid grid-cols-1 gap-x-4 md:grid-cols-2">
            <SelectField
              label="Evento"
              name="consumptionEvent"
              value={event}
              iconKey="clock"
              options={momentOptions}
              onChange={(e) =>
                setEvent(e.target.value as RestaurantInventoryConsumptionMoment)
              }
            />
            <InputField
              label="Tipo de referencia"
              name="referenceType"
              type="text"
              value={referenceType}
              required
              onChange={(e) => setReferenceType(e.target.value)}
            />
            <InputField
              label="ID de referencia"
              name="referenceId"
              type="number"
              min={1}
              value={referenceId}
              required
              error={!referenceId ? 'Ingrese la referencia.' : undefined}
              onChange={(e) => setReferenceId(e.target.value)}
            />
            <div>
              <label className="mb-3 block text-black dark:text-white">
                Notas
              </label>
              <textarea
                rows={2}
                maxLength={500}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary dark:border-strokedark"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              data-testid="consume-button"
              type="submit"
              disabled={
                consuming ||
                !validQuantity ||
                !referenceId ||
                !availability?.canProceed
              }
              className="inline-flex items-center gap-2 rounded bg-primary px-5 py-2.5 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {consuming && <Loader2 className="h-4 w-4 animate-spin" />}
              Registrar consumo
            </button>
          </div>
          {!availability && (
            <p className="mt-2 text-right text-sm text-bodydark2">
              Verifique disponibilidad antes de consumir.
            </p>
          )}
          {availability && !availability.canProceed && (
            <p className="mt-2 text-right text-sm text-danger">
              Acción bloqueada: inventario insuficiente.
            </p>
          )}
        </form>
      )}

      {consumption && (
        <div
          className="rounded-xl border border-stroke p-4 dark:border-strokedark"
          data-testid="consumption-result"
        >
          <h3 className="font-semibold text-black dark:text-white">
            Resultado del consumo
          </h3>
          {!consumption.consumed ? (
            <div className="mt-3 rounded-lg bg-primary/10 p-3 text-primary">
              <strong>No se generaron movimientos.</strong>
              <p>
                {consumption.skippedReason ||
                  'El consumo fue omitido por la configuración actual.'}
              </p>
            </div>
          ) : (
            <>
              <p className="mt-1 text-sm text-bodydark2">
                Costo total:{' '}
                <strong>{formatMoney(consumption.totalCost)}</strong>
              </p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[680px] text-left text-sm">
                  <thead className="bg-gray-2 dark:bg-meta-4">
                    <tr>
                      <th className="p-3">Materia prima</th>
                      <th className="p-3">Anterior</th>
                      <th className="p-3">Nuevo</th>
                      <th className="p-3">Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consumption.movements.map((movement) => (
                      <tr
                        key={movement.id}
                        className="border-b border-stroke dark:border-strokedark"
                      >
                        <td className="p-3">{movement.rawMaterialName}</td>
                        <td className="p-3">
                          {formatDecimal(movement.previousStock)}{' '}
                          {movement.unitCode}
                        </td>
                        <td
                          className={`p-3 ${
                            compareDecimal(movement.newStock, 0) < 0
                              ? 'font-semibold text-danger'
                              : ''
                          }`}
                        >
                          {formatDecimal(movement.newStock)} {movement.unitCode}
                        </td>
                        <td className="p-3">
                          {formatMoney(movement.totalCost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {consumption.lowStockAlerts.length > 0 && (
                <div className="mt-3 rounded-lg bg-warning/10 p-3 text-warning">
                  <strong>Alertas de stock mínimo</strong>
                  <ul className="mt-1 list-inside list-disc text-sm">
                    {consumption.lowStockAlerts.map((alert) => (
                      <li key={alert.rawMaterialId}>
                        {alert.rawMaterialName}:{' '}
                        {formatDecimal(alert.currentStock)} {alert.unitCode}{' '}
                        (mínimo {formatDecimal(alert.minimumStock)})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default RestaurantRecipeProductionPanel;
